/**
 * React Query hooks for DataSource management API.
 * All queries are scoped to the current tenant.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient, { getErrorMessage } from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  DataSource,
  DataSourceCreate,
  DataSourceUpdate,
  DataSourceListResponse,
  SyncRunListResponse,
  BindRequest,
  OAuthInitiateResponse,
  OAuthCallbackResponse,
  OAuthProvider,
} from '../types';

// =============================================================================
// Query Keys
// =============================================================================

export const datasourceKeys = {
  all: (tenantId: string | undefined) => ['datasources', tenantId] as const,
  lists: (tenantId: string | undefined) => [...datasourceKeys.all(tenantId), 'list'] as const,
  list: (tenantId: string | undefined, params: Record<string, unknown>) =>
    [...datasourceKeys.lists(tenantId), params] as const,
  details: (tenantId: string | undefined) => [...datasourceKeys.all(tenantId), 'detail'] as const,
  detail: (tenantId: string | undefined, id: string) =>
    [...datasourceKeys.details(tenantId), id] as const,
  syncRuns: (tenantId: string | undefined, id: string) =>
    [...datasourceKeys.detail(tenantId, id), 'sync-runs'] as const,
};

// =============================================================================
// API Functions
// =============================================================================

async function fetchDataSources(params: Record<string, unknown> = {}): Promise<DataSourceListResponse> {
  const { data } = await apiClient.get<DataSourceListResponse>('/api/v1/datasources', { params });
  return data;
}

async function fetchDataSource(id: string): Promise<DataSource> {
  const { data } = await apiClient.get<DataSource>(`/api/v1/datasources/${id}`);
  return data;
}

async function createDataSource(input: DataSourceCreate): Promise<DataSource> {
  const { data } = await apiClient.post<DataSource>('/api/v1/datasources', input);
  return data;
}

async function updateDataSource(id: string, input: DataSourceUpdate): Promise<DataSource> {
  const { data } = await apiClient.patch<DataSource>(`/api/v1/datasources/${id}`, input);
  return data;
}

async function deleteDataSource(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/datasources/${id}`);
}

async function bindDataSource(id: string, input: BindRequest): Promise<DataSource> {
  const { data } = await apiClient.post<DataSource>(`/api/v1/datasources/${id}/bind`, input);
  return data;
}

async function unbindDataSource(id: string): Promise<DataSource> {
  const { data } = await apiClient.post<DataSource>(`/api/v1/datasources/${id}/unbind`);
  return data;
}

async function triggerSync(id: string): Promise<void> {
  await apiClient.post(`/api/v1/datasources/${id}/sync`);
}

async function triggerRescan(id: string): Promise<void> {
  await apiClient.post(`/api/v1/datasources/${id}/rescan`);
}

async function pauseDataSource(id: string): Promise<DataSource> {
  const { data } = await apiClient.post<DataSource>(`/api/v1/datasources/${id}/pause`);
  return data;
}

async function resumeDataSource(id: string): Promise<DataSource> {
  const { data } = await apiClient.post<DataSource>(`/api/v1/datasources/${id}/resume`);
  return data;
}

async function fetchSyncRuns(id: string, params: Record<string, unknown> = {}): Promise<SyncRunListResponse> {
  const { data } = await apiClient.get<SyncRunListResponse>(`/api/v1/datasources/${id}/sync-runs`, { params });
  return data;
}

// =============================================================================
// Query Hooks
// =============================================================================

export function useDataSources(params: Record<string, unknown> = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: datasourceKeys.list(tenantId, params),
    queryFn: () => fetchDataSources(params),
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });
}

export function useDataSource(id: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: datasourceKeys.detail(tenantId, id),
    queryFn: () => fetchDataSource(id),
    enabled: !!id && !!tenantId,
    staleTime: 30 * 1000,
  });
}

export function useSyncRuns(sourceId: string, params: Record<string, unknown> = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: datasourceKeys.syncRuns(tenantId, sourceId),
    queryFn: () => fetchSyncRuns(sourceId, params),
    enabled: !!sourceId && !!tenantId,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

export function useCreateDataSource() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: createDataSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: datasourceKeys.lists(tenantId) });
    },
    onError: (error) => {
      console.error('Failed to create datasource:', getErrorMessage(error));
    },
  });
}

export function useUpdateDataSource() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: ({ id, ...input }: DataSourceUpdate & { id: string }) => updateDataSource(id, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(datasourceKeys.detail(tenantId, updated.id), updated);
      queryClient.invalidateQueries({ queryKey: datasourceKeys.lists(tenantId) });
    },
    onError: (error) => {
      console.error('Failed to update datasource:', getErrorMessage(error));
    },
  });
}

export function useDeleteDataSource() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: deleteDataSource,
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: datasourceKeys.detail(tenantId, deletedId) });
      queryClient.invalidateQueries({ queryKey: datasourceKeys.lists(tenantId) });
    },
    onError: (error) => {
      console.error('Failed to delete datasource:', getErrorMessage(error));
    },
  });
}

export function useBindDataSource() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: ({ id, ...input }: BindRequest & { id: string }) => bindDataSource(id, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(datasourceKeys.detail(tenantId, updated.id), updated);
      queryClient.invalidateQueries({ queryKey: datasourceKeys.lists(tenantId) });
    },
  });
}

export function useUnbindDataSource() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: unbindDataSource,
    onSuccess: (updated) => {
      queryClient.setQueryData(datasourceKeys.detail(tenantId, updated.id), updated);
      queryClient.invalidateQueries({ queryKey: datasourceKeys.lists(tenantId) });
    },
  });
}

export function useTriggerSync() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: triggerSync,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: datasourceKeys.detail(tenantId, id) });
      queryClient.invalidateQueries({ queryKey: datasourceKeys.syncRuns(tenantId, id) });
    },
  });
}

export function useTriggerRescan() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: triggerRescan,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: datasourceKeys.detail(tenantId, id) });
      queryClient.invalidateQueries({ queryKey: datasourceKeys.syncRuns(tenantId, id) });
    },
  });
}

export function usePauseDataSource() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: pauseDataSource,
    onSuccess: (updated) => {
      queryClient.setQueryData(datasourceKeys.detail(tenantId, updated.id), updated);
      queryClient.invalidateQueries({ queryKey: datasourceKeys.lists(tenantId) });
    },
  });
}

export function useResumeDataSource() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: resumeDataSource,
    onSuccess: (updated) => {
      queryClient.setQueryData(datasourceKeys.detail(tenantId, updated.id), updated);
      queryClient.invalidateQueries({ queryKey: datasourceKeys.lists(tenantId) });
    },
  });
}

// =============================================================================
// OAuth API Functions
// =============================================================================

async function initiateOAuth(
  provider: OAuthProvider,
  scopes?: string[],
  datasourceProvider?: string,
): Promise<OAuthInitiateResponse> {
  const { data } = await apiClient.post<OAuthInitiateResponse>(
    '/api/v1/datasources/oauth/initiate',
    {
      provider,
      ...(datasourceProvider ? { datasource_provider: datasourceProvider } : {}),
      ...(scopes ? { scopes } : {}),
    },
  );
  return data;
}

async function callbackOAuth(code: string, state: string): Promise<OAuthCallbackResponse> {
  const { data } = await apiClient.post<OAuthCallbackResponse>(
    '/api/v1/datasources/oauth/callback',
    { code, state },
  );
  return data;
}

// =============================================================================
// OAuth Hooks
// =============================================================================

export function useOAuthInitiate() {
  return useMutation({
    mutationFn: ({
      provider,
      scopes,
      datasourceProvider,
    }: {
      provider: OAuthProvider;
      scopes?: string[];
      datasourceProvider?: string;
    }) => initiateOAuth(provider, scopes, datasourceProvider),
  });
}

export function useOAuthCallback() {
  return useMutation({
    mutationFn: ({ code, state }: { code: string; state: string }) =>
      callbackOAuth(code, state),
  });
}
